import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import toast from "react-hot-toast";

import { rsvpApi } from "../../api/rsvp";
import { QueryErrorBanner, TableSkeletonRows } from "../../components/feedback/QueryFeedback";
import { APP_WORKSPACE } from "../../constants/appWorkspace";
import { useAuth } from "../../context/AuthContext";
import { queryKeys } from "../../queryKeys";
import type { PromoterGuest } from "../../types/rsvp";
import "./GuestListPage.css";

const GuestListPage: React.FC = () => {
  const { token } = useAuth();
  const workspace = APP_WORKSPACE;
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [editingPhone, setEditingPhone] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editInstagram, setEditInstagram] = useState("");

  const guestsQuery = useQuery({
    queryKey: queryKeys.promoterGuests(workspace),
    queryFn: () => rsvpApi.listPromoterGuests(token!),
    enabled: Boolean(token),
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list: PromoterGuest[] = guestsQuery.data ?? [];
    if (!q) return list;
    return list.filter((g: PromoterGuest) => {
      const events = (g.event_names ?? []).join(" ").toLowerCase();
      const artist = (g.favourite_artist ?? "").toLowerCase();
      return (
        g.name.toLowerCase().includes(q) ||
        g.phone.toLowerCase().includes(q) ||
        (g.instagram ?? "").toLowerCase().includes(q) ||
        events.includes(q) ||
        artist.includes(q)
      );
    });
  }, [guestsQuery.data, search]);

  const startEdit = (guest: PromoterGuest) => {
    setEditingPhone(guest.phone);
    setEditName(guest.name);
    setEditInstagram(guest.instagram ?? "");
  };

  const saveEdit = useMutation({
    mutationFn: async () => {
      if (!editingPhone) return;
      return rsvpApi.updatePromoterGuest(
        editingPhone,
        {
          name: editName.trim(),
          instagram: editInstagram.trim() || null,
        },
        token!,
      );
    },
    onSuccess: async () => {
      toast.success("Guest updated");
      setEditingPhone(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.promoterGuests(workspace) });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Update failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: async (phone: string) => {
      await rsvpApi.deletePromoterGuest(phone, token!);
    },
    onSuccess: async () => {
      toast.success("Guest removed");
      setEditingPhone(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.promoterGuests(workspace) });
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Delete failed"),
  });

  return (
    <div className="guest-list-page">
      <header className="guest-list-page__header">
        <h1 className="app-shell-page-title">Guest List</h1>
        <label className="guest-list-page__search">
          <span className="visually-hidden">Search guests</span>
          <input
            className="eventos-input"
            placeholder="Search name, number, Instagram, events, artist…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </header>

      {guestsQuery.isError ? (
        <QueryErrorBanner
          message={guestsQuery.error instanceof Error ? guestsQuery.error.message : "Failed to load guests"}
          onRetry={() => void guestsQuery.refetch()}
        />
      ) : null}

      <div className="guest-list-page__table-wrap">
        {/* `--stack` renders each row as a labelled card below the md tier, so six
            columns don't force a sideways scroll on a phone. */}
        <table className="app-shell-table app-shell-table--stack guest-list-page__table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Number</th>
              <th>Instagram</th>
              <th>Events Attended</th>
              <th>Favourite Artist</th>
              <th className="guest-list-page__actions-head">Edit</th>
            </tr>
          </thead>
          <tbody>
            {guestsQuery.isPending ? (
              <TableSkeletonRows rows={8} cols={6} />
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="guest-list-page__empty">
                  No guests yet. RSVPs from your events will appear here.
                </td>
              </tr>
            ) : (
              rows.map((guest) => (
                <tr key={guest.phone}>
                  <td data-label="Name">
                    {editingPhone === guest.phone ? (
                      <input
                        className="eventos-input"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        aria-label="Guest name"
                      />
                    ) : (
                      guest.name
                    )}
                  </td>
                  <td className="eventos-table__num" data-label="Number">{guest.phone}</td>
                  <td data-label="Instagram">
                    {editingPhone === guest.phone ? (
                      <input
                        className="eventos-input"
                        value={editInstagram}
                        onChange={(e) => setEditInstagram(e.target.value)}
                        aria-label="Instagram"
                      />
                    ) : (
                      guest.instagram || "—"
                    )}
                  </td>
                  <td data-label="Events Attended">
                    <span className="guest-list-page__events" title={guest.event_names.join(", ") || undefined}>
                      {guest.events_attended}
                    </span>
                  </td>
                  <td data-label="Favourite Artist">{guest.favourite_artist || "—"}</td>
                  <td data-label="Edit">
                    <div className="guest-list-page__row-actions">
                      {editingPhone === guest.phone ? (
                        <>
                          <button
                            type="button"
                            className="eventos-primary-btn"
                            onClick={() => saveEdit.mutate()}
                            disabled={saveEdit.isPending}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="eventos-nav-btn eventos-nav-btn--danger"
                            onClick={() => {
                              if (window.confirm(`Delete ${guest.name}?`)) deleteMutation.mutate(guest.phone);
                            }}
                          >
                            Delete
                          </button>
                          <button type="button" className="eventos-nav-btn" onClick={() => setEditingPhone(null)}>
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button type="button" className="eventos-nav-btn" onClick={() => startEdit(guest)}>
                          Edit
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default GuestListPage;
